import React, { useState, useEffect } from 'react';
import { HelpCircle, X, ArrowRight, CheckCircle, MessageCircle } from 'lucide-react';
import { useAuth } from '../lib/AuthContext';

interface TourGuideProps {
  onOpenAssistant: () => void;
  onSetAssistantInitialMessage: (message: string) => void;
}

export function TourGuide({ onOpenAssistant, onSetAssistantInitialMessage }: TourGuideProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const { user } = useAuth();

  // Check if this is a new user or first visit
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('hasSeenTour');
    if (!hasSeenTour && !dismissed) {
      // Wait a moment before showing the tour guide
      const timer = setTimeout(() => {
        setIsOpen(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [dismissed]);

  const handleDismiss = () => {
    setIsOpen(false);
    setDismissed(true);
    localStorage.setItem('hasSeenTour', 'true');
  };

  const handleNext = () => {
    if (currentStep < tourSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Last step - mark as completed
      handleDismiss();
    }
  };

  const handleOpenAssistant = (message: string) => {
    onSetAssistantInitialMessage(message);
    onOpenAssistant();
    handleDismiss();
  };

  const tourSteps = [
    {
      title: "Welcome to Agent Store!",
      content: "This platform helps you discover, deploy, and connect AI agents. Let me guide you through the key features.",
      action: () => handleOpenAssistant("Hi! I'm your AI assistant for Agent Store. Can you give me a quick overview of what this platform does and how to get started?")
    },
    {
      title: "Discover AI Agents",
      content: "Browse our collection of AI agents from GitHub, Hugging Face, and other sources. Use filters to find exactly what you need.",
      action: () => handleOpenAssistant("What types of AI agents can I find on this platform, and how do I search for specific ones?")
    },
    {
      title: "Agent-to-Agent Protocol",
      content: "Connect agents together using our A2A protocol to build powerful multi-agent systems that can solve complex problems.",
      action: () => handleOpenAssistant("Can you explain what the A2A protocol is and how I can use it to connect different agents together?")
    },
    {
      title: "Smart Recommendations",
      content: "Get personalized agent recommendations based on your needs and preferences.",
      action: () => handleOpenAssistant("How do the smart recommendations work? How does the system know which agents to suggest for me?")
    },
    {
      title: "Build & Deploy",
      content: "Create custom agents or deploy existing ones with our streamlined workflows.",
      action: () => handleOpenAssistant("What's the process for deploying an agent I find on the platform? And how can I build my own custom agent?")
    }
  ];

  if (!isOpen || !user) return null;

  const currentTourStep = tourSteps[currentStep];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 mx-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="p-2 bg-blue-100 rounded-full">
              <HelpCircle className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="font-semibold text-lg">{currentTourStep.title}</h3>
          </div>
          <button 
            onClick={handleDismiss}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-full"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <p className="text-gray-600 mb-6">{currentTourStep.content}</p>
        
        <div className="flex justify-between items-center">
          <div className="flex space-x-1">
            {tourSteps.map((_, index) => (
              <div 
                key={index}
                className={`w-2 h-2 rounded-full ${
                  index === currentStep ? 'bg-blue-600' : 'bg-gray-300'
                }`}
              />
            ))}
          </div>
          
          <div className="flex gap-3">
            <button
              onClick={() => currentTourStep.action()}
              className="flex items-center gap-1 px-3 py-1.5 text-sm border border-blue-200 text-blue-600 rounded-lg hover:bg-blue-50"
            >
              <MessageCircle className="w-4 h-4" />
              Ask About This
            </button>
            
            <button
              onClick={handleNext}
              className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {currentStep < tourSteps.length - 1 ? (
                <>
                  Next
                  <ArrowRight className="w-4 h-4" />
                </>
              ) : (
                <>
                  Got It
                  <CheckCircle className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}